import { LitElement, html, css } from 'lit';
import { base, forms } from '../styles.js';

/**
 * Built from the registered options JSON Schema rather than from a hard-coded field list, so that
 * adding an option is a product-registration change and not a release. The schema arrives from the
 * origin response untouched, which is also why this deliberately supports only the small subset of
 * JSON Schema the registration actually uses: string enums, arrays of them, booleans and strings.
 * An unrecognised property renders as a text input rather than being dropped -- an option the
 * officer cannot see is an order placed with the wrong instructions.
 *
 * In F0 the origin's optionsSchema is always null, so this renders only when a registration
 * carries options; the fixed request form lives in order-options.js.
 */
export class OrderForm extends LitElement {
  static properties = {
    schema: { type: Object },
    value: { type: Object },
  };

  static styles = [base, forms, css`
    :host { display: block; }
    select.inp[multiple] { min-height: 96px; }
    .none { margin: 0; color: var(--pbg-gray-700); font-size: 13px; }
  `];

  constructor() {
    super();
    this.schema = null;
    this.value = {};
  }

  get required() {
    return this.schema?.required ?? [];
  }

  get valid() {
    return this.required.every((name) => {
      const v = this.value?.[name];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== null && v !== '';
    });
  }

  render() {
    const properties = this.schema?.properties;
    if (!properties) return html`<p class="none">No options are registered for this product.</p>`;

    return html`
      <form @change=${this.#onChange} @submit=${(e) => e.preventDefault()}>
        ${Object.entries(properties).map(([name, spec]) => this.#field(name, spec))}
      </form>
    `;
  }

  #field(name, spec) {
    const required = this.required.includes(name);
    const label = html`<label class="flbl" for=${name}>${humanize(name)}${required ? html` <span class="req" aria-hidden="true">*</span>` : ''}</label>`;

    if (spec.type === 'array' && spec.items?.enum) {
      return html`<div class="form-row">${label}
        <select class="inp" id=${name} name=${name} data-field=${name} multiple ?required=${required}>
          ${spec.items.enum.map((v) => html`<option value=${v}>${v}</option>`)}
        </select></div>`;
    }
    if (spec.type === 'string' && spec.enum) {
      return html`<div class="form-row">${label}
        <select class="inp" id=${name} name=${name} data-field=${name}>
          ${spec.enum.map((v) => html`<option value=${v}>${v}</option>`)}
        </select></div>`;
    }
    if (spec.type === 'boolean') {
      return html`<div class="form-row">
        <label class="doc-item dashed" for=${name}>
          <input id=${name} name=${name} data-field=${name} type="checkbox" />
          <span class="doc-meta"><span class="dname">${humanize(name)}</span></span>
        </label></div>`;
    }
    if (spec.type === 'string' && spec.maxLength > 200) {
      return html`<div class="form-row">${label}
        <textarea class="inp" id=${name} name=${name} data-field=${name} maxlength=${spec.maxLength} rows="3"></textarea></div>`;
    }
    return html`<div class="form-row">${label}<input class="inp" id=${name} name=${name} data-field=${name} type="text" /></div>`;
  }

  #onChange(event) {
    const el = event.target;
    const next = { ...this.value };

    if (el.multiple) next[el.name] = [...el.selectedOptions].map((o) => o.value);
    else if (el.type === 'checkbox') next[el.name] = el.checked;
    else next[el.name] = el.value;

    this.value = next;
    this.dispatchEvent(new CustomEvent('options-changed', {
      detail: { value: this.value, valid: this.valid },
      bubbles: true, composed: true,
    }));
  }
}

/** "packageTypes" -> "Package types". The schema's names are the wire names; the officer reads words. */
function humanize(name) {
  const words = String(name).replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

customElements.define('epc-order-form', OrderForm);
